import React from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Landmark, ShieldCheck, Database, FileArchive, Fingerprint, Users, ArrowRight, Check, Lock } from "lucide-react";
import PageHeader from "@/components/PageHeader";
import { Reveal, Eyebrow } from "@/components/Reveal";
import { useTribeAuth } from "@/context/TribeAuthContext";
import { NODE_SPHERE_3D } from "@/lib/api";

const FEATURES = [
  { icon: Database, t: "Sovereign Tribal Rolls", d: "Maintain a private citizen registry — names, lineage, and enrollment numbers — held entirely under your nation's control." },
  { icon: Fingerprint, t: "Blockchain-Anchored Records", d: "Every roll entry and document is cryptographically hashed and anchored, creating a permanent, tamper-evident record." },
  { icon: FileArchive, t: "Governing Document Vault", d: "Upload and preserve constitutions, treaties, censuses, and resolutions in an encrypted, family-controlled archive." },
  { icon: ShieldCheck, t: "Data Sovereignty", d: "Your records are never controlled by external government bodies. Your nation holds the keys to its own heritage." },
  { icon: Users, t: "Dedicated Nation Account", d: "A separate, secure portal and login for your government — independent of individual member accounts." },
  { icon: Landmark, t: "A Digital Records Repository", d: "A modern, decentralized successor to the American Indian Records Repository — built for sovereign nations." },
];

const PLANS = [
  {
    lookup: "tribal_repository_monthly",
    tag: "Flexible",
    title: "Monthly",
    price: "$999",
    period: "/ month",
    features: ["Unlimited tribal roll entries", "Governing document uploads", "Blockchain-anchored records", "Dedicated nation portal", "Cancel anytime"],
  },
  {
    lookup: "tribal_repository_yearly",
    tag: "Best Value",
    title: "Annual",
    price: "$9,999",
    period: "/ year",
    highlight: true,
    features: ["Everything in Monthly", "Two months free vs. monthly", "Priority record verification", "Annual sovereignty audit", "Institutional continuity"],
  },
  {
    lookup: null,
    tag: "Perpetual",
    title: "Indefinite",
    price: "Custom",
    period: "one-time",
    features: ["Permanent, non-recurring access", "Perpetual record anchoring", "Endowment-style arrangement", "White-glove onboarding", "Contact us directly for pricing"],
  },
];

export default function TribalPortal() {
  const { tribe } = useTribeAuth();
  const navigate = useNavigate();
  const authed = tribe && tribe.tribe_id;

  return (
    <div>
      <PageHeader
        eyebrow="For Sovereign Nations · The Tribal Records Repository"
        title="A decentralized blockchain repository for tribal governments."
        subtitle="Offer your nation the permanence of a modern, cryptographically secured records repository — store your tribal rolls, governing documents, and citizen registry, held under your own sovereign control."
      />

      <section className="py-20 bg-ink">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 grid lg:grid-cols-2 gap-16 items-center">
          <Reveal>
            <Eyebrow>The Service</Eyebrow>
            <h2 className="font-serif font-light text-4xl tracking-tight mt-5">
              The American Indian Records Repository — reimagined on blockchain.
            </h2>
            <div className="space-y-6 mt-8 font-sans text-[#A19D98] leading-relaxed">
              <p>
                For generations, the records of aboriginal and indigenous nations were held in federal repositories — physical,
                distant, and controlled by others. The 7 Generations Indigenous Trust offers sovereign nations
                a different path: a private, decentralized digital repository where your government stores and
                anchors its own records.
              </p>
              <p>
                Just as an individual anchors their lineage in the Vault, a nation anchors its
                <span className="text-gold-text"> tribal rolls, citizen registry, and governing documents</span> —
                each entry cryptographically sealed and permanently preserved.
              </p>
            </div>
          </Reveal>
          <Reveal delay={0.15}>
            <div className="border border-gold/20 overflow-hidden bg-ink relative">
              <motion.img
                src={NODE_SPHERE_3D}
                alt="Decentralized tribal records repository"
                className="w-full h-[360px] object-cover"
                animate={{ scale: [1, 1.06, 1] }}
                transition={{ duration: 18, repeat: Infinity, ease: "easeInOut" }}
              />
              <div className="absolute bottom-0 inset-x-0 bg-gradient-to-t from-ink to-transparent p-6">
                <p className="font-mono text-[10px] tracking-[0.22em] uppercase text-gold/70">Sovereign Registry · Decentralized Nodes</p>
              </div>
            </div>
          </Reveal>
        </div>
      </section>

      <section className="py-20 bg-ink-deep">
        <div className="max-w-7xl mx-auto px-6 lg:px-10">
          <Reveal><Eyebrow>Repository Capabilities</Eyebrow></Reveal>
          <div className="grid md:grid-cols-3 gap-8 mt-12">
            {FEATURES.map((f, i) => (
              <Reveal key={f.t} delay={i * 0.08}>
                <div className="border border-gold/15 bg-ink p-8 h-full hover:border-gold/40 transition-colors duration-500">
                  <f.icon size={26} className="text-gold" strokeWidth={1.3} />
                  <h3 className="font-serif text-2xl mt-6">{f.t}</h3>
                  <p className="font-sans text-sm text-[#A19D98] leading-relaxed mt-4">{f.d}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <section className="py-24 bg-ink" data-testid="portal-pricing">
        <div className="max-w-6xl mx-auto px-6 lg:px-10">
          <Reveal>
            <Eyebrow>Subscription Plans</Eyebrow>
            <h2 className="font-serif font-light text-4xl sm:text-5xl tracking-tight mt-5 max-w-2xl">
              Choose your nation's arrangement.
            </h2>
          </Reveal>
          <div className="grid md:grid-cols-3 gap-8 mt-14">
            {PLANS.map((p, i) => (
              <Reveal key={p.title} delay={i * 0.1}>
                <div className={`border p-8 sm:p-10 h-full flex flex-col ${p.highlight ? "border-gold/50 bg-ink-surface" : "border-gold/15 bg-ink"}`}>
                  <p className="font-mono text-[10px] tracking-[0.26em] uppercase text-gold/60">{p.tag}</p>
                  <h3 className="font-serif text-3xl mt-1">{p.title}</h3>
                  <div className="flex items-baseline gap-2 mt-4">
                    <span className="font-serif text-4xl text-gold-gradient">{p.price}</span>
                    <span className="font-mono text-[11px] tracking-[0.14em] uppercase text-[#7a766f]">{p.period}</span>
                  </div>
                  <ul className="mt-8 space-y-4 flex-1">
                    {p.features.map((f) => (
                      <li key={f} className="flex items-start gap-3">
                        <Check size={16} className="text-gold mt-0.5 shrink-0" />
                        <span className="font-sans text-sm text-[#C9C4BD]">{f}</span>
                      </li>
                    ))}
                  </ul>
                  {p.lookup ? (
                    <button
                      data-testid={`plan-${p.title.toLowerCase()}-cta`}
                      onClick={() => navigate(authed ? "/tribes/dashboard" : `/tribes/login?mode=register&plan=${p.lookup}`)}
                      className="mt-10 flex items-center justify-center gap-3 px-6 py-4 bg-gold text-black font-mono text-[11px] tracking-[0.2em] uppercase hover:bg-gold-text transition-colors"
                    >
                      {authed ? "Subscribe in Dashboard" : "Get Started"} <ArrowRight size={14} />
                    </button>
                  ) : (
                    <a
                      href="mailto:7gentrust@niijiglobal.com?subject=Indefinite%20Tribal%20Records%20Repository%20Subscription"
                      data-testid="plan-indefinite-cta"
                      className="mt-10 flex items-center justify-center gap-3 px-6 py-4 border border-gold/50 text-gold font-mono text-[11px] tracking-[0.2em] uppercase hover:bg-gold hover:text-black transition-colors"
                    >
                      Contact Us Directly <ArrowRight size={14} />
                    </a>
                  )}
                </div>
              </Reveal>
            ))}
          </div>
          <Reveal delay={0.2}>
            <p className="font-sans text-sm text-[#A19D98] mt-8 text-center">
              For indefinite / perpetual arrangements, contact us directly at{" "}
              <a href="mailto:7gentrust@niijiglobal.com" className="text-gold hover:underline" data-testid="indefinite-email">7gentrust@niijiglobal.com</a>.
            </p>
          </Reveal>
          <Reveal delay={0.25}>
            <p className="font-mono text-[11px] tracking-[0.3em] uppercase text-gold/50 mt-12 text-center">
              Sovereign · Encrypted · Blockchain-Anchored
            </p>
          </Reveal>
        </div>
      </section>

      <section className="py-24 bg-ink-deep border-t border-gold/15" data-testid="portal-cta">
        <div className="max-w-3xl mx-auto px-6 lg:px-10 text-center">
          <Reveal>
            <Landmark size={30} className="text-gold mx-auto" strokeWidth={1.2} />
            <h2 className="font-serif font-light text-4xl sm:text-5xl tracking-tight mt-6">
              Establish your nation's sovereign registry.
            </h2>
            <p className="font-sans text-base text-[#A19D98] leading-relaxed mt-6 max-w-xl mx-auto">
              Create a secure nation account to begin, or sign in to your existing repository.
            </p>
            <div className="flex flex-wrap justify-center gap-4 mt-10">
              <button
                data-testid="portal-register-cta"
                onClick={() => navigate(authed ? "/tribes/dashboard" : "/tribes/login?mode=register")}
                className="group inline-flex items-center gap-3 px-8 py-4 bg-gold text-black font-mono text-[11px] tracking-[0.2em] uppercase hover:bg-gold-text transition-colors"
              >
                {authed ? "Open Nation Dashboard" : "Register Your Nation"} <ArrowRight size={14} className="group-hover:translate-x-1 transition-transform" />
              </button>
              {!authed && (
                <Link
                  to="/tribes/login"
                  data-testid="portal-login-cta"
                  className="inline-flex items-center gap-2 px-8 py-4 border border-gold/50 text-gold font-mono text-[11px] tracking-[0.2em] uppercase hover:bg-gold hover:text-black transition-colors"
                >
                  <Lock size={13} /> Tribal Login
                </Link>
              )}
            </div>
          </Reveal>
        </div>
      </section>
    </div>
  );
}
